import { useEffect, useRef, type ReactNode } from "react";
import { NavLink, useParams } from "react-router-dom";
import { Heart, Users, User } from "lucide-react";

import { SiteHeader, SiteFooter } from "@/components/layout/SiteShell";
import { DashboardTopbar } from "@/components/layout/DashboardTopbar";
import { EventParticipantSidebar } from "@/components/layout/EventParticipantSidebar";
import { useAuthUser } from "@/lib/use-auth-user";
import { signInParticipantByEmail } from "@/lib/api/participants";
import { getParticipantSession, saveParticipantSession } from "@/lib/participant-session";
import { cn } from "@/lib/utils";

interface ParticipantShellProps {
  children: ReactNode;
  matchesCount?: number;
  hideNav?: boolean;
  /** Optional left-side slot for the SiteHeader (e.g. "1 / 3" counter pill). */
  topLeft?: ReactNode;
  /** Optional right-side slot for the SiteHeader (e.g. user-menu pill). */
  topRight?: ReactNode;
  /** Drop the default page padding so the page can paint the column edge-to-edge. */
  fullBleed?: boolean;
  /** Use the full available height for the column (no global SiteFooter). */
  fillViewport?: boolean;
  /** Override the inner column max-width (default 480px) — legacy mobile-only. */
  maxWidth?: number;
}

/**
 * Layout for in-event participant pages.
 *
 * Two flavours, picked at runtime by whether the viewer is signed in to
 * Supabase:
 *
 *   1. Authenticated → Dashboard chrome (topbar + left rail with the
 *      same navigation items they already see on /me/* pages). Lets the
 *      user jump between events, the global matches list, or sign out
 *      without losing context. Triggered by useAuthUser returning a user.
 *
 *   2. Anonymous (legacy /e/:slug/join flow — only localStorage session)
 *      → mobile-first column with a bottom tab bar. Required for the
 *      no-signup "enter your email, get a code, take part" path.
 */
export function ParticipantShell({
  children,
  matchesCount,
  hideNav,
  topLeft,
  topRight,
  fullBleed,
  fillViewport,
  maxWidth = 480,
}: ParticipantShellProps) {
  const { slug } = useParams<{ slug: string }>();
  const { user, loading } = useAuthUser();

  // If the Supabase auth user's email does not match the participant
  // session stored in localStorage for this slug, try to relink. Symptom
  // we hit in production: the sidebar showed k.ionov@hapic.com (the
  // signed-in account) while the page content rendered for konsion@list.ru
  // (whatever email was used at the per-event /e/:slug/join step).
  //
  // We only attempt the switch once per slug per page load — the
  // `attemptedFor` ref guards against an infinite retry if the lookup
  // fails or the page re-renders before the reload kicks in.
  const attemptedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!user?.email || !slug) return;
    const key = `${slug}::${user.email}`;
    if (attemptedFor.current === key) return;
    attemptedFor.current = key;

    const current = getParticipantSession(slug);
    if (!current || current.email.toLowerCase() === user.email.toLowerCase()) return;

    let cancelled = false;
    void (async () => {
      try {
        const aligned = await signInParticipantByEmail(slug, user.email);
        if (cancelled || !aligned) return;
        // Only swap if we actually found a participant for this email in
        // this event — otherwise leave the old session alone (the user
        // may legitimately participate under a different address).
        saveParticipantSession(slug, {
          participantId: aligned.participantId,
          eventId: aligned.eventId,
          name: aligned.name,
          email: aligned.email,
        });
        if (typeof window !== "undefined") window.location.reload();
      } catch {
        // Silent — RPC failures here shouldn't block the page.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.email, slug]);

  // Authenticated users get the persistent Dashboard chrome around the page.
  // Skip while we're still resolving the session to avoid a flash of the
  // legacy mobile layout for users who are actually logged in.
  if (loading) {
    return <div className="min-h-screen bg-[#fafafa]" />;
  }

  if (user && slug) {
    return (
      <div className="min-h-screen bg-[#F0F2F5]">
        <DashboardTopbar />
        <div className="md:grid md:min-h-[calc(100vh-64px)] md:grid-cols-[260px_1fr_300px]">
          <EventParticipantSidebar slug={slug} matchesCount={matchesCount} />
          <main
            className={cn(
              "min-w-0 px-4 py-6 pb-24 md:px-10 md:py-10 md:pb-10",
              fullBleed && "px-0 md:px-0",
            )}
          >
            {children}
          </main>
        </div>
      </div>
    );
  }

  // Anonymous participant — legacy mobile-first shell.
  const base = slug ? `/e/${slug}` : "";

  return (
    <div
      className={cn(
        "flex w-full flex-col bg-[#fafafa]",
        fillViewport ? "h-screen overflow-hidden" : "min-h-screen",
      )}
    >
      <SiteHeader leftSlot={topLeft} rightSlot={topRight} />
      <div className="flex flex-1 w-full justify-center overflow-hidden">
        <div
          className="flex w-full flex-col bg-[#fafafa]"
          style={{ maxWidth }}
        >
          <main className={cn("flex-1", fullBleed ? "" : "px-4 py-6", fillViewport && "overflow-hidden")}>
            {children}
          </main>

          {!hideNav ? (
            <nav className="sticky bottom-0 z-20 flex border-t border-[#e2e8f0] bg-white">
              {slug ? <TabLink to={`${base}/feed`} label="Feed" icon={Heart} /> : null}
              {slug ? (
                <TabLink
                  to={`${base}/matches`}
                  label={matchesCount && matchesCount > 0 ? `Matches (${matchesCount})` : "Matches"}
                  icon={Users}
                />
              ) : null}
              <TabLink to={slug ? `${base}/profile` : "/me"} label="Profile" icon={User} />
            </nav>
          ) : null}
        </div>
      </div>
      {fillViewport ? null : <SiteFooter />}
    </div>
  );
}

function TabLink({
  to,
  label,
  icon: Icon,
}: {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        cn(
          "flex flex-1 flex-col items-center justify-center gap-1 py-2.5 text-[11px] font-semibold no-underline",
          isActive ? "text-[#2563eb]" : "text-[#94a3b8]",
        )
      }
    >
      <Icon className="h-5 w-5" />
      {label}
    </NavLink>
  );
}
